"use client";

import React, { useEffect, useState } from "react";
import { usersApi } from "@/lib/api/services/users.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Mail, Shield, Calendar, MapPin } from "lucide-react";
import Link from "next/link";
import { formatDate } from "@/lib/utils/date";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  avatarUrl?: string;
  organization?: string;
  location?: string;
  joinedAt?: string;
  bio?: string;
}

export default function ProfilePage() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await usersApi.getMe();
        setUser(data.user as unknown as User);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">Profile</h1>
        <div className="mt-6 space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-muted/10 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">Profile</h1>
        <Card>
          <CardContent className="py-16 text-center">
            <UserPlus className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <h3 className="mt-4 text-base font-medium text-foreground">Profile not found</h3>
            <p className="mt-1 text-sm text-muted-foreground">Unable to load your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">Profile</h1>
        <Button variant="outline" size="sm" asChild>
          <Link href="/settings/profile">Edit Profile</Link>
        </Button>
      </div>

      <Card className="mt-6">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-5">
            <Avatar src={user.avatarUrl} alt={`${user.firstName} ${user.lastName}`} size="xl" fallback={`${user.firstName?.[0] ?? ""} ${user.lastName?.[0] ?? ""}`} />
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-display font-semibold tracking-tight text-foreground">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {user.email}
              </p>
              {user.organization && (
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {user.organization}
                </p>
              )}
              {user.joinedAt && (
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Joined {formatDate(user.joinedAt)}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="outline" className="h-5 px-1.5 font-medium capitalize">{user.role}</Badge>
                <Badge variant="success" className="h-5 px-1.5 font-medium capitalize">
                  {user.status}
                </Badge>
              </div>
              {user.bio && (
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{user.bio}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
